/*
 * PowerPoint live-deck ops normalizer (pure — no Office.js imports, unit-tested).
 *
 * The one add-in-side copy of the ops vocabulary. Keep in sync with:
 *   * the Wippy validator      (app.ppt:ops_schema)
 *   * the taskpane interpreter (taskpane.ts handleApplyOps)
 *   * the agent cookbook       (app.ppt.trait:ppt_toolkit prompt)
 *
 * Geometry is in POINTS on the live deck (16:9 slide = 960x540pt). The
 * insert_slides DSL (inches) is a separate vocabulary handled by dslRenderer.
 */

export type Op = { op: string; [k: string]: unknown };

export const KNOWN_OPS = new Set([
  // slide ops
  "add_slide",
  "delete_slide",
  "move_slide",
  "duplicate_slide",
  "apply_layout",
  "set_background",
  // content ops (slide omitted = most-recently-targeted slide)
  "add_text_box",
  "add_shape",
  "add_line",
  "add_image",
  "add_table",
  // shape edit ops
  "set_text",
  "set_style",
  "transform",
  "delete_shape",
  "copy_shape",
  "group",
  "ungroup",
  "z_order",
  "set_hyperlink",
  // table cell ops (shape = the table)
  "set_cell",
  "merge_cells",
  "insert_rows",
  "delete_rows",
  "insert_cols",
  "delete_cols",
  // metadata
  "set_tags",
  // deck-wide hygiene ops
  "replace_all",
  "stamp",
  "set_min_font",
  // composite graphics (expanded by the layout engines into grouped native shapes)
  "org_chart",
  "timeline",
  "process_flow",
  "structure_chart",
]);

const REQUIRED: Record<string, string[]> = {
  delete_slide: ["slide"],
  move_slide: ["slide", "index"],
  duplicate_slide: ["slide"],
  apply_layout: ["slide", "layout"],
  set_background: ["slide"],
  // `text` is NOT required — a box may instead be authored from `segments`
  // (styled runs), checked below. Requiring `text` here silently dropped
  // segments-only boxes and produced an empty shape.
  add_text_box: ["x", "y", "w", "h"],
  add_shape: ["shape_type", "x", "y", "w", "h"],
  add_line: ["x1", "y1", "x2", "y2"],
  add_image: ["x", "y", "w", "h"],
  add_table: ["x", "y", "w", "h", "values"],
  set_text: ["shape"],
  set_style: ["shape"],
  transform: ["shape"],
  delete_shape: ["shape"],
  copy_shape: ["from_slide", "shape", "x", "y", "w", "h"],
  group: ["shapes"],
  ungroup: ["shape"],
  z_order: ["shape", "position"],
  set_hyperlink: ["shape", "url"],
  set_cell: ["shape", "row", "col"],
  merge_cells: ["shape", "area"],
  insert_rows: ["shape", "at", "count"],
  delete_rows: ["shape", "at", "count"],
  insert_cols: ["shape", "at", "count"],
  delete_cols: ["shape", "at", "count"],
  set_tags: ["tags"],
  replace_all: ["find", "replace"],
  stamp: ["text"],
  org_chart: ["nodes"],
  timeline: ["events"],
  process_flow: ["steps"],
  structure_chart: ["entities", "ownership"],
};

const NON_STYLE_KEYS = new Set(["op", "slide", "shape"]);

export type NormalizeResult =
  { ok: true; ops: Op[] } | { ok: false; index: number; message: string };

function compositeError(op: any): string | null {
  if (op.op === "org_chart") {
    const nodes = op.nodes;
    if (!Array.isArray(nodes) || nodes.length === 0)
      return "org_chart nodes must be a non-empty list";
    if (nodes.length > 50) return "org_chart supports at most 50 nodes";
    const ids = new Set<string>();
    for (const n of nodes) {
      if (!n || typeof n !== "object" || n.id == null || n.label == null)
        return "each org_chart node needs id and label";
      ids.add(String(n.id));
    }
    let roots = 0;
    for (const n of nodes) {
      if (n.parent == null) roots++;
      else if (!ids.has(String(n.parent)))
        return `org_chart node '${n.id}' has unknown parent '${n.parent}'`;
    }
    if (roots === 0) return "org_chart needs at least one root node (no parent)";
  }
  if (op.op === "timeline") {
    const events = op.events;
    if (!Array.isArray(events) || events.length < 2) return "timeline needs at least 2 events";
    if (events.length > 20) return "timeline supports at most 20 events";
    for (const e of events) {
      if (!e || typeof e !== "object" || e.label == null)
        return "each timeline event needs a label (date recommended)";
    }
  }
  if (op.op === "process_flow") {
    const steps = op.steps;
    if (!Array.isArray(steps) || steps.length < 2) return "process_flow needs at least 2 steps";
    if (steps.length > 10) return "process_flow supports at most 10 steps";
    for (const s of steps) {
      if (!s || typeof s !== "object" || s.label == null)
        return "each process_flow step needs a label";
    }
  }
  if (op.op === "structure_chart") {
    const entities = op.entities;
    if (!Array.isArray(entities) || entities.length === 0)
      return "structure_chart entities must be a non-empty list";
    if (entities.length > 40) return "structure_chart supports at most 40 entities on one slide";
    const ids = new Set<string>();
    for (const e of entities) {
      if (!e || typeof e !== "object" || e.id == null || e.name == null)
        return "each structure_chart entity needs id and name";
      ids.add(String(e.id));
    }
    const ownership = op.ownership;
    if (!Array.isArray(ownership))
      return "structure_chart ownership must be a list of {parent,child} edges";
    for (const o of ownership) {
      if (!o || typeof o !== "object" || o.parent == null || o.child == null)
        return "each ownership edge needs parent and child";
      if (!ids.has(String(o.parent)))
        return `ownership edge references unknown parent '${o.parent}'`;
      if (!ids.has(String(o.child))) return `ownership edge references unknown child '${o.child}'`;
    }
    if (op.flows != null) {
      if (!Array.isArray(op.flows))
        return "structure_chart flows must be a list of {from,to} arrows";
      for (const f of op.flows) {
        if (!f || typeof f !== "object" || f.from == null || f.to == null)
          return "each flow needs from and to";
        if (!ids.has(String(f.from)) || !ids.has(String(f.to)))
          return `flow references an unknown entity ('${f.from}' -> '${f.to}')`;
      }
    }
  }
  return null;
}

/**
 * Validate + normalize an incoming ops array. Rejects on the FIRST bad op with
 * its index and message; the whole batch is refused pre-execution (matching
 * the Wippy-side ops_schema, which normally catches this before dispatch).
 */
export function normalizeOps(raw: unknown): NormalizeResult {
  if (!Array.isArray(raw)) return { ok: false, index: 0, message: "ops must be a list" };
  const ops: Op[] = [];
  for (let i = 0; i < raw.length; i++) {
    const op = raw[i] as any;
    if (!op || typeof op !== "object" || !op.op)
      return { ok: false, index: i, message: "missing op field" };
    if (!KNOWN_OPS.has(op.op)) return { ok: false, index: i, message: `unknown op: ${op.op}` };
    for (const f of REQUIRED[op.op] ?? []) {
      if (op[f] === undefined || op[f] === null)
        return { ok: false, index: i, message: `${op.op} missing field: ${f}` };
    }

    if (op.op === "set_background" && op.color == null && op.image_base64 == null)
      return { ok: false, index: i, message: "set_background needs color or image_base64" };
    if (op.op === "add_image" && op.png_base64 == null && op.url == null)
      return { ok: false, index: i, message: "add_image needs png_base64 or url" };
    if (
      op.op === "set_text" &&
      op.text == null &&
      op.segments == null &&
      (op.find == null || op.replace == null)
    )
      return { ok: false, index: i, message: "set_text needs text, segments, or find + replace" };
    if (op.op === "add_text_box" && op.text == null && op.segments == null)
      return { ok: false, index: i, message: "add_text_box needs text or segments" };
    if (op.op === "set_min_font") {
      const floor = op.size ?? op.fontSize;
      if (typeof floor !== "number" || !(floor > 0))
        return {
          ok: false,
          index: i,
          message: "set_min_font needs a positive numeric size (the point floor)",
        };
    }
    if (op.op === "set_style" || op.op === "transform") {
      const hasProp = Object.keys(op).some((k) => !NON_STYLE_KEYS.has(k));
      if (!hasProp)
        return { ok: false, index: i, message: `${op.op} needs at least one property to change` };
    }

    const cerr = compositeError(op);
    if (cerr) return { ok: false, index: i, message: cerr };

    ops.push(op);
  }
  return { ok: true, ops };
}
